import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import SchoolFormModal from '../components/SchoolFormModal';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isAdministration } from '../roles';

function Detail({ label, children }) {
  return (
    <div>
      <dt className="tiny muted" style={{ letterSpacing: '.08em', textTransform: 'uppercase' }}>
        {label}
      </dt>
      <dd style={{ margin: '4px 0 0' }}>{children}</dd>
    </div>
  );
}

export default function SchoolDetailPage() {
  const { schoolId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [adoptions, setAdoptions] = useState(null);
  const [codes, setCodes] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = isAdministration(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getSchool(schoolId));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    api
      .schoolAdoptions(schoolId)
      .then((result) => !cancelled && setAdoptions(result.titles))
      .catch(() => !cancelled && setAdoptions([]));
    api
      .schoolCodes(schoolId)
      .then((result) => !cancelled && setCodes(result))
      .catch(() => !cancelled && setCodes(null));
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  useEffect(() => {
    api.clientConfig().then((config) => setOptions(config.schools)).catch(() => setOptions(null));
    if (isAdmin) {
      api
        .adminListUsers({ role: 'SALES_REP', status: 'ACTIVE', size: 100 })
        .then((result) => setSalesReps(result.items))
        .catch(() => setSalesReps([]));
    }
  }, [isAdmin]);

  async function save(payload) {
    await api.updateSchool(schoolId, payload);
    setShowForm(false);
    setToast('School updated');
    await load();
  }

  async function remove() {
    setError(null);
    try {
      await api.deleteSchool(schoolId);
      navigate('/schools', { replace: true });
    } catch (err) {
      setError(err);
    }
  }

  if (loading) return <div className="empty">Loading…</div>;
  if (!data) {
    return (
      <>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/schools')}>
          <Icon name="back" size={15} />
          Back to schools
        </button>
      </>
    );
  }

  const { school, teacherCount, verifiedTeacherCount, studentCount, adoptedTitleCount, codeEditable, deletable } = data;

  return (
    <>
      <div className="page-head">
        <div>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ paddingLeft: 0, marginBottom: 8 }}
            onClick={() => navigate('/schools')}
          >
            <Icon name="back" size={15} />
            All schools
          </button>
          <h1>{school.name}</h1>
          <p className="mono">{school.code}</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--s0)' }}>
          {school.editable && (
            <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
              Edit school
            </button>
          )}
          {deletable && (
            <button type="button" className="btn btn-danger" onClick={remove}>
              Delete
            </button>
          )}
        </div>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {school.customerStatus === 'INACTIVE' && (
        <InfoBanner>
          This school is marked inactive. Its teachers, students and history are kept.
        </InfoBanner>
      )}

      <section className="card card-pad">
        <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>Profile</h2>
        <dl className="grid-2" style={{ margin: 0, gap: 'var(--s1)' }}>
          <Detail label="Board">{school.board?.name}</Detail>
          <Detail label="Customer status">{school.customerStatusLabel}</Detail>
          <Detail label="City">{school.city?.name}</Detail>
          <Detail label="State">{school.state?.name}</Detail>
          <Detail label="Assigned sales rep">
            {school.assignedSalesRep ? (
              <>
                {school.assignedSalesRep.fullName}
                <span className="muted small"> · {school.assignedSalesRep.email}</span>
              </>
            ) : (
              <span className="muted">Unassigned</span>
            )}
          </Detail>
          <Detail label="Added">{new Date(school.createdAt).toLocaleDateString()}</Detail>
        </dl>
      </section>

      <section className="card card-pad">
        <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>Adopted titles</h2>
        {adoptions === null ? (
          <p className="muted small" style={{ margin: 0 }}>Loading…</p>
        ) : adoptions.filter((title) => title.adopted).length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            This school has not adopted any titles yet.
          </p>
        ) : (
          <ul className="chip-list">
            {adoptions
              .filter((title) => title.adopted)
              .map((title) => (
                <li key={title.titleId} className="badge badge-ok">
                  {title.name}
                </li>
              ))}
          </ul>
        )}
        <p className="hint" style={{ marginTop: 'var(--s1)' }}>
          {adoptedTitleCount} adopted. Teachers at this school see resources for these titles only.
        </p>
      </section>

      {codes && codes.allocated > 0 && (
        <section className="card card-pad">
          <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>Activation codes</h2>
          <div className="stat-row">
            <div className="stat">
              <b>{codes.allocated.toLocaleString()}</b>
              <span>Allocated</span>
            </div>
            <div className="stat">
              <b>{codes.used.toLocaleString()}</b>
              <span>Used</span>
            </div>
            <div className="stat">
              <b>{codes.unused.toLocaleString()}</b>
              <span>Still unused</span>
            </div>
          </div>
          <p className="hint" style={{ marginTop: 'var(--s1)' }}>
            {codes.titles.length} title(s) with codes at this school. A used code cannot be
            reissued.
          </p>
        </section>
      )}

      <section className="card card-pad">
        <h2 style={{ fontSize: 'var(--fs-md)', marginBottom: 'var(--s1)' }}>People</h2>
        <dl className="grid-2" style={{ margin: 0, gap: 'var(--s1)' }}>
          <Detail label="Teachers">
            {teacherCount}
            <span className="muted small"> · {verifiedTeacherCount} verified</span>
          </Detail>
          <Detail label="Students">{studentCount}</Detail>
        </dl>
        {teacherCount + studentCount > 0 && (
          <p className="hint" style={{ marginTop: 'var(--s1)' }}>
            Because people are attached to this school, its code is locked and it cannot be deleted.
            Set the status to Inactive instead.
          </p>
        )}
      </section>

      {showForm && (
        <SchoolFormModal
          school={school}
          options={options}
          salesReps={salesReps}
          canAssignRep={isAdmin}
          codeEditable={codeEditable}
          onClose={() => setShowForm(false)}
          onSave={save}
        />
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}
